import { useEffect, useRef, useState } from "react";
import { Send, ShieldAlert, Info, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, API, errMsg } from "@/lib/api";
import { ScriptureRef } from "./Common";

const SAMPLES = ["What does the Bible say about worry?", "Why did Jesus speak in parables?", "How can I forgive someone who hurt me?", "What is grace?"];

export function Md({ text }: { text: string }) {
  const inline = (s: string) => s.split(/(\*\*[^*]+\*\*)/g).map((part, j) => (part.startsWith("**") && part.endsWith("**") ? <strong key={j}>{part.slice(2, -2)}</strong> : part));
  return (
    <div className="space-y-3 leading-relaxed text-slate-800" data-testid="ai-answer">
      {text.split("\n").map((l, i) => {
        const t = l.trim();
        if (!t || /^-{3,}$/.test(t)) return null;
        if (/^#{1,4}\s/.test(t)) return <p key={i} className="pt-1 font-display text-xl font-semibold text-slate-900">{inline(t.replace(/^#+\s/, ""))}</p>;
        if (/^[-*•]\s/.test(t)) return <p key={i} className="pl-5 -indent-3">• {inline(t.replace(/^[-*•]\s/, ""))}</p>;
        if (/^>\s?/.test(t)) return <p key={i} className="border-l-4 border-amber-600 bg-amber-50/70 px-3 py-1 font-scripture italic">{inline(t.replace(/^>\s?/, ""))}</p>;
        return <p key={i}>{inline(t)}</p>;
      })}
    </div>
  );
}

function Answer({ item, idx }: { item: any; idx: number }) {
  return (
    <div className="space-y-4" data-testid={`ai-exchange-${idx}`}>
      <div className="ml-auto max-w-xl rounded-2xl rounded-br-sm bg-[#1E293B] px-5 py-3 text-amber-50">{item.question}</div>
      <div className="card-warm max-w-3xl p-6">
        {item.error ? <p className="text-red-700" data-testid="ai-error">{item.error}</p> : item.answer ? <Md text={item.answer} /> : <p className="text-slate-500" data-testid="ai-answer">Searching the Scriptures…</p>}
        {item.done && !item.error && (
          <div className="mt-5 space-y-3 border-t border-[#E2DBCE] pt-4">
            {item.cited ? (
              <div><p className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Scripture cited</p>
                <div className="flex flex-wrap gap-2" data-testid="ai-citations">{item.refs.map((r: string) => <ScriptureRef key={r} refText={r} />)}</div></div>
            ) : (
              <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800" data-testid="ai-uncited-warning"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />No scripture citation was found in this answer. Treat it as unverified, not as spiritual authority.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AIHelper({ initial }: { initial?: string }) {
  const [q, setQ] = useState(initial || "");
  const [items, setItems] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  useEffect(() => { api.get("/ai/history").then((r) => setItems(r.data.slice(0, 5).reverse().map((h: any) => ({ ...h, done: true })))); }, []);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [items.length]);

  const patch = (fn: (a: any) => any) => setItems((p) => [...p.slice(0, -1), fn(p[p.length - 1])]);
  const ask = async (question: string) => {
    if (!question.trim() || busy) return;
    setBusy(true);
    setQ("");
    setItems((p) => [...p, { question, answer: "", done: false }]);
    try {
      await api.get("/auth/me");
      const res = await fetch(`${API}/ai/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ question }) });
      if (!res.ok || !res.body) {
        const d = await res.json().catch(() => ({}));
        patch((a) => ({ ...a, done: true, error: errMsg({ response: { data: d } }) }));
        return;
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          try {
            const ev = JSON.parse(p.replace(/^data: /, ""));
            if (ev.t === "delta") patch((a) => ({ ...a, answer: a.answer + ev.c }));
            else if (ev.t === "done") patch((a) => ({ ...a, done: true, cited: ev.cited, refs: ev.refs }));
            else if (ev.t === "error") patch((a) => ({ ...a, done: true, error: ev.message }));
          } catch { /* partial chunk */ }
        }
      }
    } catch (e) {
      patch((a) => ({ ...a, done: true, error: errMsg(e) }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <aside className="space-y-5 lg:col-span-4">
        <div className="rounded-2xl bg-[#1E293B] p-6 text-amber-50">
          <Sparkles className="h-6 w-6 text-amber-300" />
          <h3 className="mt-3 font-display text-2xl">Cast Study Helper</h3>
          <p className="mt-2 text-sm text-slate-300">Answers must cite the King James Version. Where Christian traditions differ, it shows the passages rather than taking sides.</p>
        </div>
        <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50/70 p-4 text-sm text-amber-950" data-testid="ai-disclaimer"><Info className="mt-0.5 h-4 w-4 shrink-0" />AI responses are study aids. Always verify with Scripture and your church community.</p>
        <div className="flex flex-wrap gap-2">{SAMPLES.map((s, i) => <button key={s} data-testid={`ai-sample-${i}`} onClick={() => ask(s)} className="chip text-left transition-colors hover:bg-[#E2DBCE]">{s}</button>)}</div>
      </aside>
      <div className="space-y-6 lg:col-span-8">
        {items.length === 0 && <p className="text-slate-500">Ask a question about a passage, a character, or a topic.</p>}
        {items.map((it, i) => <Answer key={i} item={it} idx={i} />)}
        <div ref={end} />
        <div className="sticky bottom-24 flex gap-3 rounded-2xl border border-[#E2DBCE] bg-white p-3 shadow-lg lg:bottom-6">
          <Textarea data-testid="ai-question-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="e.g. What does Romans 8 teach about suffering?" className="min-h-[52px] resize-none border-0 shadow-none focus-visible:ring-0"
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(q); } }} />
          <Button data-testid="ai-study-submit-button" disabled={busy || q.trim().length < 3} onClick={() => ask(q)} className="h-auto rounded-xl bg-[#C86D51] px-5 hover:bg-[#B35A3F]"><Send className="h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
